import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { UtilService } from '../../core/services/util.service';
import { useAuthStore } from '../../core/store/auth.store';
import { useRouteStore } from '../../core/store/route.store';
import { useProfile } from './modules/profile/components/ProfileContext';
import { PreloadService } from '../../core/services/preload.service';
import { Spinner } from '../../core/components';
import { route } from '../../constants/routes';
import { Breadcrumb, Layout, Menu, Avatar, Dropdown, Drawer } from 'antd';
import { GetBreadCrumb, GetSelected } from './util';
import React, { useEffect, useState, useRef } from 'react';
import type { MenuProps } from 'antd';
import {
  DesktopOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PieChartOutlined,
  ProfileOutlined,
  SolutionOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  SafetyOutlined,
  CalendarOutlined,
  StarOutlined,
} from '@ant-design/icons';
import SecurityAvailabilityModal from '../public/components/login/SecurityAvailabilityModal';
import { AuthService } from '../public/services/auth-login.service';
import ThemeToggle from '../../core/components/ThemeToggle';
import PoweredByFooter from '../../core/components/PoweredByFooter';
import SiteBrand from '../../core/components/SiteBrand';
import RecruiterCopilot, {
  getRecruiterCopilotContext,
} from './modules/recruiter-copilot/components/RecruiterCopilot';
import RecruiterNotificationCenter from './modules/notification/components/RecruiterNotificationCenter';
import { isSiteAdmin } from '../../core/auth/roles';

const { Header, Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

/** Below Ant Design `lg` — use drawer nav instead of fixed collapsed sider. */
const MOBILE_NAV_BREAKPOINT = 992;
const COMPACT_BREAKPOINT = 425;
/** Dense Ops icon rail (direction E) — collapsed sidebar width in px. */
const SIDER_EXPANDED_WIDTH = 200;
const SIDER_ICON_RAIL_WIDTH = 56;

const PrivateLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, resetAuth } = useAuthStore();
  const { resetRoutePrefix } = useRouteStore();
  const [transition, setTransition] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const siderRef = useRef<HTMLDivElement>(null);
  const [securityAvailabilityVisible, setSecurityAvailabilityVisible] =
    useState(false);
  const [userHasTotp, setUserHasTotp] = useState(false);
  const [checkingTotpStatus, setCheckingTotpStatus] = useState(true);
  const siteAdmin = isSiteAdmin(auth?.payload?.realm_access?.roles);

  const utils = UtilService.GetInstance();
  const [isMobileNav, setIsMobileNav] = useState(
    window.innerWidth < MOBILE_NAV_BREAKPOINT
  );
  const [isCompact, setIsCompact] = useState(
    window.innerWidth <= COMPACT_BREAKPOINT
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const { profile, companyId, companyName } = useProfile();
  const clientName = location.state?.clientName || null;
  const candidateName = location.state?.candidateName || null;
  const stageName = location.state?.stageName || null;
  const breadcrumbs = GetBreadCrumb(
    location,
    companyName,
    clientName,
    candidateName || undefined,
    stageName || undefined
  );
  const isPrivacyPage = location.pathname === route('/privacy');
  const isTermsPage = location.pathname === route('/terms');
  const legalPageBreadcrumb = isPrivacyPage
    ? 'Privacy Policy'
    : isTermsPage
      ? 'Terms and Conditions'
      : null;
  const [photo, setPhoto] = useState<string>('');
  const [logo, setLogo] = useState<string>('');
  const isPreview = location.pathname.includes('/preview');
  const copilotContext = getRecruiterCopilotContext(
    location.pathname,
    location.state
  );

  useEffect(() => {
    if (!profile?.id) return;

    const photoKey = `profilePhoto_${profile.id}`;
    const localPhoto = localStorage.getItem(photoKey);
    setPhoto(localPhoto || '');
  }, [profile?.id]);

  useEffect(() => {
    if (!companyId) return;

    const logoKey = `companyLogo_${companyId}`;
    const localLogo = localStorage.getItem(logoKey);
    setLogo(localLogo || '');
  }, [companyId]);

  // Check user's TOTP status when component mounts
  useEffect(() => {
    const checkUserTotpStatus = async () => {
      const username = auth?.payload?.preferred_username || auth?.payload?.sub;
      if (!username) {
        setCheckingTotpStatus(false);
        return;
      }

      try {
        const response = await AuthService.CheckTotpStatus(username);
        setUserHasTotp(response.hasTotpConfigured);

        // If user has TOTP configured, clean up their security availability record
        if (response.hasTotpConfigured) {
          try {
            await AuthService.CleanupSecurityAvailabilityForTotpUser(username);
            console.log(
              'Cleaned up security availability record for TOTP user:',
              username
            );
          } catch (cleanupError) {
            console.warn(
              'Failed to cleanup security availability record:',
              cleanupError
            );
            // Don't fail the main flow if cleanup fails
          }
        }
      } catch (error) {
        console.error('Error checking user TOTP status:', error);
        // Default to false if check fails
        setUserHasTotp(false);
      } finally {
        setCheckingTotpStatus(false);
      }
    };

    checkUserTotpStatus();
  }, [auth?.payload?.preferred_username, auth?.payload?.sub]);

  const GetItem = (
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    onClick?: () => void,
    children?: MenuItem[]
  ): MenuItem => ({
    label,
    key,
    icon,
    onClick: onClick
      ? () => {
          onClick();
          if (isMobileNav) {
            setMobileMenuOpen(false);
          }
        }
      : undefined,
    children,
  });

  const menuItems: MenuItem[] = [
    GetItem('Dashboard', route('/dashboard'), <PieChartOutlined />, () =>
      navigate(route('/dashboard'))
    ),
    GetItem('Company', route('/company'), <BankOutlined />, () =>
      navigate(route('/company'))
    ),
    GetItem('Candidates', route('/candidates'), <TeamOutlined />, () =>
      navigate(route('/candidates'))
    ),
    GetItem('Jobs', route('/job'), <DesktopOutlined />, () =>
      navigate(route('/job'))
    ),
    GetItem(
      'Job Applications',
      route('/job-applications'),
      <SolutionOutlined />,
      () => navigate(route('/job-applications'))
    ),
    GetItem(
      'Interview Agenda',
      route('/interviews/agenda'),
      <CalendarOutlined />,
      () => navigate(route('/interviews/agenda'))
    ),
    ...(siteAdmin
      ? [
          GetItem(
            'Recommendations',
            route('/site-recommendations'),
            <StarOutlined />,
            () => navigate(route('/site-recommendations'))
          ),
        ]
      : []),
  ];

  useEffect(() => {
    const handleResize = () => {
      const mobileNav = window.innerWidth < MOBILE_NAV_BREAKPOINT;
      setIsMobileNav(mobileNav);
      setIsCompact(window.innerWidth <= COMPACT_BREAKPOINT);
      if (mobileNav) {
        setCollapsed(true);
        setMobileMenuOpen(false);
      } else {
        setCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!auth) {
      navigate('/login');
    }

    const subscription = utils.OnTransition().subscribe((transitionState) => {
      setTransition(transitionState);
    });

    return () => subscription.unsubscribe();
  }, [auth, navigate, utils]);

  const siderExpanded = !collapsed;
  const desktopSiderWidth = siderExpanded
    ? SIDER_EXPANDED_WIDTH
    : SIDER_ICON_RAIL_WIDTH;

  const companyLogo = (
    <Avatar
      size={isMobileNav ? 48 : siderExpanded ? 72 : 40}
      icon={<BankOutlined />}
      shape="square"
      className="border-2 border-gray-200"
      src={
        logo ? (
          <img
            src={logo}
            alt="Company logo"
            onLoad={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              const ratio = img.naturalWidth / img.naturalHeight;
              img.style.objectFit =
                ratio > 0.8 && ratio < 1.2 ? 'cover' : 'contain';
              img.style.padding = ratio > 0.8 && ratio < 1.2 ? '0px' : '6px';
            }}
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        ) : undefined
      }
    />
  );

  const navigationMenu = (
    <Menu
      theme="light"
      selectedKeys={[GetSelected(location)]}
      mode="inline"
      inlineCollapsed={!isMobileNav && collapsed}
      items={menuItems}
      className="admin-nav-menu"
    />
  );

  const toggleNavigation = () => {
    if (isMobileNav) {
      setMobileMenuOpen(true);
      return;
    }
    setCollapsed(!collapsed);
  };

  // Security Availability Modal Handlers
  const handleSecurityAvailabilityConfigure = () => {
    // Redirect to Keycloak using the same URL pattern as OAuth2 login
    const username = auth?.payload?.preferred_username || auth?.payload?.sub;
    if (username) {
      setSecurityAvailabilityVisible(false);

      // First, enable OTP setup for the user via our backend (same as Login component)
      AuthService.SetupOtp(username)
        .then(() => {
          // After enabling OTP setup, redirect to Keycloak login
          // The user will login first, then be prompted to setup OTP due to the required action
          const keycloakBaseUrl = 'https://user-admin.onrender.com/auth';
          const realm = 'find-job';
          const clientId = 'job-management-client';
          const redirectUri = encodeURIComponent(window.location.origin);

          // Use the same OAuth2 URL pattern as login for consistency
          const keycloakUrl = `${keycloakBaseUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;

          // Redirect in same window
          window.location.href = keycloakUrl;
        })
        .catch((error) => {
          console.error('Error setting up OTP:', error);
          UtilService.Alert(
            'Error',
            'error',
            'Unable to setup OTP. Please try again later.'
          );
        });
    } else {
      UtilService.Alert(
        'Error',
        'error',
        'Unable to determine user information for 2FA setup.'
      );
    }
  };

  const handleSecurityAvailabilityDismiss = async () => {
    const username = auth?.payload?.preferred_username || auth?.payload?.sub;
    if (username) {
      try {
        // Mark as shown for 7 days - maybe later
        await AuthService.MarkSecurityAvailabilityAsShown(username, 7);
        setSecurityAvailabilityVisible(false);
        UtilService.Alert(
          '2FA Setup',
          'info',
          "You can setup 2FA anytime from the profile menu. We'll remind you again in a week."
        );
      } catch (error) {
        console.error('Error marking security availability as shown:', error);
        setSecurityAvailabilityVisible(false);
      }
    }
  };

  const handleSecurityAvailabilityClose = async () => {
    const username = auth?.payload?.preferred_username || auth?.payload?.sub;
    if (username) {
      try {
        // Mark as shown for 1 month (30 days) - don't show for a month
        await AuthService.MarkSecurityAvailabilityAsShown(username, 30);
        setSecurityAvailabilityVisible(false);
        UtilService.Alert(
          '2FA Setup',
          'info',
          "You can setup 2FA anytime from the profile menu. We'll remind you again in a month."
        );
      } catch (error) {
        console.error(
          'Error marking security availability for 1 month:',
          error
        );
        setSecurityAvailabilityVisible(false);
      }
    }
  };

  const handleSecurityAvailabilityCancel = () => {
    // Just close the modal without any action - user clicked X button
    setSecurityAvailabilityVisible(false);
  };

  return (
    <Layout className="admin-app-shell admin-app-shell--dense">
      {!isPreview && !isMobileNav ? (
        <Sider
          ref={siderRef}
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          collapsedWidth={SIDER_ICON_RAIL_WIDTH}
          width={SIDER_EXPANDED_WIDTH}
          trigger={null}
          className={`admin-sider desktop-sider${collapsed ? ' admin-sider--icon-rail' : ''}`}
          style={{
            height: '100vh',
            position: 'fixed',
            insetInlineStart: 0,
            top: 0,
            bottom: 0,
            scrollbarWidth: 'thin',
            scrollbarGutter: 'stable',
            zIndex: 1000,
          }}
        >
          <div className="admin-sider__shell">
            <div
              className={`admin-sider__brand flex justify-center ${collapsed ? 'my-3 mx-1' : 'm-4'}`}
            >
              {companyLogo}
            </div>
            <div className="admin-sider__nav">{navigationMenu}</div>
            <PoweredByFooter collapsed={collapsed} />
          </div>
        </Sider>
      ) : null}

      {!isPreview && isMobileNav ? (
        <Drawer
          title={companyName || 'Navigation'}
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={280}
          styles={{ body: { padding: 0 } }}
          className="mobile-nav-drawer"
        >
          <div className="mobile-nav-drawer__shell">
            <div className="flex justify-center border-b border-white/10 px-4 py-4">
              {companyLogo}
            </div>
            <div className="mobile-nav-drawer__nav">{navigationMenu}</div>
            <PoweredByFooter className="admin-powered-by--drawer" />
          </div>
        </Drawer>
      ) : null}

      {isPreview ? (
        <header
          className="job-preview-chrome flex items-center py-3 shadow-md w-full fixed top-0 left-0"
          style={{ zIndex: 1000 }}
        >
          <div className="container-main flex flex-row items-center w-full justify-between gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => window.close()}
              className="job-preview-chrome__close flex items-center px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg text-xs sm:text-sm font-semibold"
            >
              Close Preview
            </button>
            <SiteBrand
              markSize={36}
              className="job-preview-chrome__brand mx-auto"
              wordmarkClassName="job-preview-chrome__wordmark"
            />
            <div className="hidden sm:block w-20" aria-hidden="true" />
          </div>
        </header>
      ) : null}

      <Layout
        style={{
          marginInlineStart: !isPreview && !isMobileNav ? desktopSiderWidth : 0,
          transition: 'margin 0.2s ease',
        }}
      >
        {!isPreview && (
          <Header
            className="admin-top-header"
            style={{
              padding: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingInline: isCompact ? '8px' : '24px',
            }}
          >
            <div className="flex min-w-0 flex-1 items-center justify-start gap-2 sm:gap-4">
              {!isPreview
                ? React.createElement(
                    isMobileNav || collapsed
                      ? MenuUnfoldOutlined
                      : MenuFoldOutlined,
                    {
                      className: 'trigger shrink-0',
                      onClick: toggleNavigation,
                      style: { fontSize: '18px', cursor: 'pointer' },
                      title: isMobileNav
                        ? 'Open navigation'
                        : collapsed
                          ? 'Expand sidebar'
                          : 'Collapse to icon rail',
                    }
                  )
                : null}
              <Breadcrumb
                className="ms-1 min-w-0 truncate sm:ms-4"
                items={
                  legalPageBreadcrumb
                    ? [{ key: 'legal', title: legalPageBreadcrumb }]
                    : breadcrumbs.map((breadcrumb, index) => ({
                        key: index,
                        title: breadcrumb.path ? (
                          <Link to={breadcrumb.path} state={breadcrumb.state}>
                            {breadcrumb.label}
                          </Link>
                        ) : (
                          breadcrumb.label
                        ),
                      }))
                }
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ThemeToggle />
              <RecruiterNotificationCenter />
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'profile',
                      icon: <ProfileOutlined />,
                      label: 'Profile',
                      onClick: () => navigate(route('/profile')),
                    },
                    // Only show "Setup 2FA" if user doesn't have TOTP configured and we're not still checking
                    ...(!checkingTotpStatus && !userHasTotp
                      ? [
                          {
                            key: 'setup-2fa',
                            icon: <SafetyOutlined />,
                            label: 'Setup 2FA',
                            onClick: () => {
                              // Show the security availability modal for consistent UX
                              setSecurityAvailabilityVisible(true);
                            },
                          },
                        ]
                      : []),
                    {
                      key: 'logout',
                      icon: <LogoutOutlined />,
                      label: 'Logout',
                      onClick: () => {
                        PreloadService.clearPreloadedData();
                        resetAuth();
                        resetRoutePrefix();
                        navigate('/login');
                      },
                    },
                  ],
                }}
                trigger={isMobileNav ? ['click'] : ['hover']}
                placement="bottom"
              >
                <button
                  type="button"
                  className="ac-dropdown-trigger"
                  aria-label="Account menu"
                >
                  <div className="flex items-center">
                    <Avatar
                      size={isCompact ? 32 : 45}
                      src={photo || undefined}
                      icon={<UserOutlined />}
                      shape="circle"
                      className="hover:border-blue-400 cursor-pointer"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                </button>
              </Dropdown>
            </div>
          </Header>
        )}
        <div className="relative">
          <Spinner visible={transition} />
          <div
            className={`ac-page-outlet${isPreview ? ' container-main' : ''}`}
          >
            <Outlet />
          </div>
        </div>
      </Layout>

      {/* Security Availability Modal */}
      <SecurityAvailabilityModal
        visible={securityAvailabilityVisible}
        onClose={handleSecurityAvailabilityCancel}
        onConfigure={handleSecurityAvailabilityConfigure}
        onDismiss={handleSecurityAvailabilityDismiss}
        onDontShowAgain={handleSecurityAvailabilityClose}
      />
      {!isPreview ? (
        <RecruiterCopilot
          companyId={companyId || undefined}
          context={copilotContext}
          open={copilotOpen}
          onOpenChange={setCopilotOpen}
        />
      ) : null}
    </Layout>
  );
};

export default PrivateLayout;
